import React, { useEffect, useState } from 'react';
import './App.css';
import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { loginRequest } from './authConfig';
import { Outlet } from 'react-router-dom';
import styles from './App.module.scss';
import * as signalR from '@microsoft/signalr';

interface AppArgs {
    msalInstance: PublicClientApplication;
}

export type LoggedInUser = {
    logoutHandler: () => void;
    getAccessToken: () => Promise<string>;
    userId: string;
}

export interface LoggedInAppArgs {
    loggedInUser: LoggedInUser;
    setModalContent: (content: ModalContent | null) => void;
    scapeConnection?: signalR.HubConnection;
    // setModalContent: (content: React.JSX.Element, canCloseModal?: boolean) => void;
}

export interface ModalContent {
    element: React.JSX.Element;
    canClose?: boolean;
}


const App: React.FC<AppArgs> = (args) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [account, setAccount] = useState<AccountInfo | null>(null);
    const [modalQueue, setModalQueue] = useState<ModalContent[]>([]);
    const [scapeConnection, setScapeConnection] = useState<signalR.HubConnection | null>(null);
    const [userConnection, setUserConnection] = useState<signalR.HubConnection | null>(null);

    const modalContent = (modalQueue.length > 0) ? modalQueue[0] : null;
    const closeModal = () => {
        setModalQueue(prev => prev.slice(1));
    }
    const setModalContent = (content: ModalContent | null) => {
        if (content === null) {
            closeModal();
            return;
        }
        setModalQueue(prev => [...prev.filter(c => c.canClose), content]);
    };

    useEffect(() => {
        if (!account) {
            return;
        }

        const getAccessToken = async (): Promise<string> => {
            // cache current token and return first if valid??
            try {
                const response = await args.msalInstance.acquireTokenSilent({
                    ...loginRequest,
                    account,
                });
                return response.accessToken;
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    const response = await args.msalInstance.acquireTokenPopup(loginRequest);
                    return response.accessToken;
                }
                throw error;
            }
        }
        let userConnection = new signalR.HubConnectionBuilder()
            .withUrl("/rpc/user", {
                // THIS SHOULD REFERENCE ACCESS TOKEN FACTORY INSTEAD OF SINGLE STRING
                accessTokenFactory: () => getAccessToken(),
                transport: signalR.HttpTransportType.WebSockets
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();


        let scapeConnection: signalR.HubConnection;
        scapeConnection = new signalR.HubConnectionBuilder()
            .withUrl("/rpc/scape", {
                // THIS SHOULD REFERENCE ACCESS TOKEN FACTORY INSTEAD OF SINGLE STRING
                accessTokenFactory: () => getAccessToken(),
                transport: signalR.HttpTransportType.WebSockets
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

        userConnection.start()
            .then(() => {
                console.log("✅ Connected to User SignalR hub");
                setUserConnection(userConnection);
                return scapeConnection.start()
            })
            .then(() => {
                console.log("✅ Connected to Scape SignalR hub");
                setScapeConnection(scapeConnection);
            })
            .catch(err => {
                console.error("❌ Scape SignalR Connection Error: ", err);
                // setError(err);
            });
    }, [account]);

    useEffect(() => {
        // ✅ Properly initialize MSAL first
        args.msalInstance.initialize()
            .then(() => {
                setIsInitialized(true);

                // ✅ Now handle redirect
                args.msalInstance.handleRedirectPromise()
                    .then((result: AuthenticationResult | null) => {
                        if (result) {
                            args.msalInstance.setActiveAccount(result.account);
                            setAccount(result.account);
                            console.log("🟢 Login successful:", result.account?.username);
                        } else {
                            const account = args.msalInstance.getAllAccounts()[0];
                            if (account) {
                                args.msalInstance.setActiveAccount(account);
                                console.log("🔄 Active session found:", account.username);
                            }
                        }
                    }).catch(error => {
                        console.error("❌ Error during redirect handling:", error);
                    });
            }).catch(error => {
                console.error("❌ Failed to initialize MSAL:", error);
            });
    }, []);

    useEffect(() => {
        if (!scapeConnection) {
            return;
        }
        scapeConnection.onreconnecting(error => {
            console.log("Connection lost with scape hub due to error:", error);
        });

        scapeConnection.onreconnected(connectionId => {
            console.log("Reconnected to scape hub with new connection ID:", connectionId);
            // scapeConnection.invoke("Rejoin", { GameId: gameId });
        });

        scapeConnection.on("OnQuickMatchScapePrepared", (result: any) => {
            console.log("Quick Match has been prepared!");
            console.log(result);
        });
    }, [scapeConnection]);

    useEffect(() => {
        if (!userConnection) {
            return;
        }
        userConnection.invoke('GetMe', {})
            .then(result => {
                console.log("Got user data.");
                console.log(result);
                return userConnection.invoke('GetMyInventory', {})
            })
            .then(result => {
                console.log("Got inventory.");
                console.log(result);
            })
            .catch(e => {
                console.log("error getting user data.");
                console.log(e);
            });
    }, [userConnection]);

    if (account) {
        const logoutHandler = () => {
            args.msalInstance.logoutRedirect();
        }

        const getAccessToken = async (): Promise<string> => {
            // cache current token and return first if valid??
            try {
                const response = await args.msalInstance.acquireTokenSilent({
                    ...loginRequest,
                    account,
                });
                return response.accessToken;
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    const response = await args.msalInstance.acquireTokenPopup(loginRequest);
                    return response.accessToken;
                }
                throw error;
            }
        }
        const context: LoggedInAppArgs = {
            loggedInUser: {
                getAccessToken,
                logoutHandler,
                userId: account.localAccountId
            },
            setModalContent,
            scapeConnection
        }
        const modalUI = (modalContent !== null)
            ? <div className={styles.msModalContainer}>
                <div className={styles.msOverlay} onClick={e => {
                    if (!modalContent.canClose) {
                        return;
                    }
                    e.preventDefault();
                    closeModal();
                }}></div>
                <div className={styles.msModal}>
                    {modalContent.element}
                </div>
            </div> : null;
        return <>
            {modalUI}
            <div className={styles.userSection}>
                <div>
                    <p>logged in as {account.username} {account.idTokenClaims.name} with Mirrorscape user id: {account.idTokenClaims.oid}</p>
                </div>
                <div>
                    <button onClick={e => {
                        e.preventDefault();
                        logoutHandler();
                    }}>Sign Out</button>
                </div>
            </div>
            <Outlet context={context} />
        </>
    }

    if (!isInitialized) return <div>🔄 Initializing...</div>;

    return <div className={styles.appRoot}>
        <h1>Welcome, please sign in.</h1>
        <button onClick={() => args.msalInstance.loginRedirect(loginRequest)}>
            Sign In
        </button>
    </div>
};

export default App;
